import { createContext, useContext, useReducer, ReactNode } from 'react'
import {
  DreamityAccountResDto,
  DreamityAccountSaveReqDto,
  DreamityAccountModifyReqDto,
  DreamityDepositReqDto,
  DreamityWithdrawReqDto,
  DreamityTransactionResDto,
} from '../types/account.types'

interface AccountState {
  accounts: DreamityAccountResDto[]
  selectedAccount: DreamityAccountResDto | null
  transactions: DreamityTransactionResDto[]
  loading: boolean
  error: string | null
}

type AccountAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ACCOUNTS'; payload: DreamityAccountResDto[] }
  | { type: 'SET_SELECTED_ACCOUNT'; payload: DreamityAccountResDto | null }
  | { type: 'UPDATE_ACCOUNT_IN_LIST'; payload: DreamityAccountResDto }
  | { type: 'DELETE_ACCOUNT'; payload: number }
  | { type: 'SET_TRANSACTIONS'; payload: DreamityTransactionResDto[] }

interface AccountContextType extends AccountState {
  fetchAccounts: (dreamId: string) => Promise<void>
  createAccount: (dreamId: string, account: DreamityAccountSaveReqDto) => Promise<void>
  updateAccount: (dreamId: string, accountId: number, account: DreamityAccountModifyReqDto) => Promise<void>
  deleteAccount: (dreamId: string, accountId: number) => Promise<void>
  deposit: (dreamId: string, accountId: number, deposit: DreamityDepositReqDto) => Promise<void>
  withdraw: (dreamId: string, accountId: number, withdraw: DreamityWithdrawReqDto) => Promise<void>
  fetchTransactions: (dreamId: string, accountId: number) => Promise<void>
  // 계좌 선택
  selectAccount: (account: DreamityAccountResDto | null) => void
}

const AccountContext = createContext<AccountContextType | undefined>(undefined)

const initialState: AccountState = {
  accounts: [],
  selectedAccount: null,
  transactions: [],
  loading: false,
  error: null,
}

function accountReducer(
  state: AccountState,
  action: AccountAction
): AccountState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_ACCOUNTS':
      return { ...state, accounts: action.payload }
    case 'SET_SELECTED_ACCOUNT':
      return { ...state, selectedAccount: action.payload }
    case 'UPDATE_ACCOUNT_IN_LIST':
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.accountId === action.payload.accountId
            ? action.payload
            : account
        ),
        // 선택된 계좌도 업데이트
        selectedAccount:
          state.selectedAccount?.accountId === action.payload.accountId
            ? action.payload
            : state.selectedAccount,
      }
    case 'DELETE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.filter(
          (account) => account.accountId !== action.payload
        ),
        // 삭제된 계좌가 선택된 계좌이면 선택 해제
        selectedAccount:
          state.selectedAccount?.accountId === action.payload
            ? null
            : state.selectedAccount,
      }
    case 'SET_TRANSACTIONS':
      return { ...state, transactions: action.payload }
    default:
      return state
  }
}

interface AccountProviderProps {
  children: ReactNode
}

export function AccountProvider({ children }: AccountProviderProps) {
  const [state, dispatch] = useReducer(accountReducer, initialState)

  /**
   * 1. 계좌 전체 조회
   * 로그인한 사용자의 모든 계좌 조회
   */
  const fetchAccounts = async (_dreamId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    dispatch({ type: 'SET_ACCOUNTS', payload: [] })
    dispatch({ type: 'SET_LOADING', payload: false })
  }

  const createAccount = async (_dreamId: string, _account: DreamityAccountSaveReqDto) => {
    console.warn('[Account] 로컬 전용: 계좌 API를 사용하지 않습니다.')
  }

  const updateAccount = async (
    _dreamId: string,
    _accountId: number,
    _account: DreamityAccountModifyReqDto
  ) => {
    console.warn('[Account] 로컬 전용: 계좌 API를 사용하지 않습니다.')
  }

  const deleteAccount = async (_dreamId: string, _accountId: number) => {
    console.warn('[Account] 로컬 전용: 계좌 API를 사용하지 않습니다.')
  }

  const deposit = async (_dreamId: string, _accountId: number, _deposit: DreamityDepositReqDto) => {
    console.warn('[Account] 로컬 전용: 계좌 API를 사용하지 않습니다.')
  }

  const withdraw = async (_dreamId: string, _accountId: number, _withdraw: DreamityWithdrawReqDto) => {
    console.warn('[Account] 로컬 전용: 계좌 API를 사용하지 않습니다.')
  }

  const fetchTransactions = async (_dreamId: string, _accountId: number) => {
    dispatch({ type: 'SET_TRANSACTIONS', payload: [] })
  }

  /**
   * 계좌 선택
   */
  const selectAccount = (account: DreamityAccountResDto | null) => {
    dispatch({ type: 'SET_SELECTED_ACCOUNT', payload: account })
  }

  return (
    <AccountContext.Provider
      value={{
        ...state,
        fetchAccounts,
        createAccount,
        updateAccount,
        deleteAccount,
        deposit,
        withdraw,
        fetchTransactions,
        selectAccount,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}

// useAccount hook은 hooks/useAccount.ts에서 export합니다
export function useAccountContext() {
  const context = useContext(AccountContext)
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider')
  }
  return context
}

